export const FEED_KINDS = ['needs-you', 'plan', 'finished', 'decision', 'watching', 'memory', 'routine', 'update', 'away', 'team-run', 'team-task', 'team-change', 'org-push', 'team-node', 'working', 'work', 'pr', 'dispatch', 'app'];
/** How far one press of useful / not useful moves a kind, and how far it may go either way. */
export const WEIGHT_STEP = 1;
export const WEIGHT_LIMIT = 3;
/** How long a hidden card stays hidden when nothing about it changed. */
export const HIDE_MS = 7 * 24 * 60 * 60 * 1000;
export const PREFS_KEY = 'mju.home.feedPrefs';
/** At most this many cards are kept as done; the oldest are forgotten first (and come back if they are still there). */
export const DONE_LIMIT = 500;
/** The dock holds at most this many pinned apps. */
export const DOCK_PINS = 8;
/** At most this many starred apps are kept. */
export const FAVOURITE_LIMIT = 200;
export const EMPTY_PREFS = { weights: {}, pinned: [], hidden: {}, pins: [], favourites: [], done: {} };
const clamp = (n) => Math.max(-WEIGHT_LIMIT, Math.min(WEIGHT_LIMIT, n));
/** What storage held, made safe: anything unreadable becomes the default. */
export function normalisePrefs(raw) {
    if (!raw || typeof raw !== 'object')
        return EMPTY_PREFS;
    const r = raw;
    const weights = {};
    if (r.weights && typeof r.weights === 'object') {
        for (const kind of FEED_KINDS) {
            const v = r.weights[kind];
            if (typeof v === 'number' && Number.isFinite(v) && Math.round(v) !== 0)
                weights[kind] = clamp(Math.round(v));
        }
    }
    const pinned = Array.isArray(r.pinned) ? [...new Set(r.pinned.filter((x) => typeof x === 'string'))] : [];
    const hidden = {};
    if (r.hidden && typeof r.hidden === 'object') {
        for (const [id, e] of Object.entries(r.hidden)) {
            const entry = e;
            if (entry && typeof entry.until === 'number' && typeof entry.changeKey === 'string')
                hidden[id] = { until: entry.until, changeKey: entry.changeKey };
        }
    }
    const pins = Array.isArray(r.pins) ? [...new Set(r.pins.filter((x) => typeof x === 'string' && x.length > 0 && x.length <= 60))].slice(0, DOCK_PINS) : [];
    const favourites = Array.isArray(r.favourites) ? [...new Set(r.favourites.filter((x) => typeof x === 'string' && x.length > 0 && x.length <= 60))].slice(0, FAVOURITE_LIMIT) : [];
    const done = {};
    if (r.done && typeof r.done === 'object') {
        const entries = Object.entries(r.done).filter((e) => {
            const v = e[1];
            return !!v && typeof v.digest === 'string' && typeof v.at === 'number' && Number.isFinite(v.at);
        });
        for (const [id, e] of entries.sort((a, b) => b[1].at - a[1].at).slice(0, DONE_LIMIT))
            done[id] = { digest: e.digest, at: e.at };
    }
    return { weights, pinned, hidden, pins, favourites, done };
}
/** Useful raises the kind's weight by a step, not useful lowers it; the same press again does not run away. */
export function rateKind(prefs, kind, verdict) {
    const next = clamp((prefs.weights[kind] ?? 0) + (verdict === 'useful' ? WEIGHT_STEP : -WEIGHT_STEP));
    const weights = { ...prefs.weights };
    if (next === 0)
        delete weights[kind];
    else
        weights[kind] = next;
    return { ...prefs, weights };
}
export function setPinned(prefs, id, on) {
    const rest = prefs.pinned.filter((p) => p !== id);
    return { ...prefs, pinned: on ? [...rest, id] : rest };
}
/** Star or unstar an app: a new star goes first. */
export function setFavourite(prefs, id, on) {
    const rest = prefs.favourites.filter((f) => f !== id);
    return { ...prefs, favourites: on ? [id, ...rest].slice(0, FAVOURITE_LIMIT) : rest };
}
export function hideItem(prefs, id, changeKey, now) {
    return { ...setPinned(prefs, id, false), hidden: { ...prefs.hidden, [id]: { until: now + HIDE_MS, changeKey } } };
}
/** A hidden card is gone until a week passes or its record changes. */
export function isHidden(prefs, id, changeKey, now) {
    const h = prefs.hidden[id];
    return !!h && now < h.until && h.changeKey === changeKey;
}
/** Entries that expired are dropped, so storage does not grow for ever. */
export function pruneHidden(prefs, now) {
    const hidden = Object.fromEntries(Object.entries(prefs.hidden).filter(([, h]) => now < h.until));
    return Object.keys(hidden).length === Object.keys(prefs.hidden).length ? prefs : { ...prefs, hidden };
}
export function loadPrefs(store) {
    try {
        const text = store?.getItem(PREFS_KEY);
        return text ? normalisePrefs(JSON.parse(text)) : EMPTY_PREFS;
    }
    catch {
        return EMPTY_PREFS;
    }
}
/** Which way a kind leans: what the pressed state of useful / not useful shows. */
export function ratingOf(prefs, kind) {
    const w = prefs.weights[kind] ?? 0;
    return w > 0 ? 'useful' : w < 0 ? 'not-useful' : undefined;
}
/**
 * A press on useful / not useful as a toggle: pressing the one that is already on clears it; the
 * other one (or none yet) leans the kind one step that way.
 */
export function toggleRating(prefs, kind, verdict) {
    const weights = { ...prefs.weights };
    if (ratingOf(prefs, kind) === verdict)
        delete weights[kind];
    else
        weights[kind] = verdict === 'useful' ? WEIGHT_STEP : -WEIGHT_STEP;
    return { ...prefs, weights };
}
/** A hidden or done card brought back: the way back from "Hidden for a week" and from Done. */
export function showItem(prefs, id) {
    return showItems(prefs, [id]);
}
/** Cards brought back at once: a cleared batch's one Undo, or one row's "Show again". */
export function showItems(prefs, ids) {
    const gone = ids.filter((id) => prefs.hidden[id] || prefs.done?.[id]);
    if (!gone.length)
        return prefs;
    const hidden = { ...prefs.hidden };
    const done = { ...prefs.done };
    for (const id of gone) {
        delete hidden[id];
        delete done[id];
    }
    return { ...prefs, hidden, done };
}
/** What a card says, as a short digest: its change key and its title. The time words and a running clock are not in it. */
export function feedDigest(item) {
    // FNV-1a, 32 bits: enough to tell one card's words from its next ones, and small to keep.
    let h = 0x811c9dc5;
    const text = `${item.changeKey}\n${item.title}`;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
}
/** Done: these cards leave until what they say changes. A pinned one is unpinned, as Hide does. */
export function doneItems(prefs, items, now) {
    if (!items.length)
        return prefs;
    const done = { ...prefs.done };
    const ids = new Set(items.map((i) => i.id));
    for (const i of items)
        done[i.id] = { digest: feedDigest(i), at: now };
    return { ...prefs, pinned: prefs.pinned.filter((p) => !ids.has(p)), done };
}
/** A done card is gone while it still says what it said when it was put away. */
export function isDone(prefs, item) {
    const d = prefs.done?.[item.id];
    return !!d && d.digest === feedDigest(item);
}
/** Returns whether it was kept: false from a store that refused. */
export function savePrefs(store, prefs) {
    try {
        store?.setItem(PREFS_KEY, JSON.stringify(prefs));
        return !!store;
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=prefsModel.js.map